import { useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

type Props = {
  value: string; // YYYY-MM-DD or '' for no selection
  onChange: (value: string) => void;
  label?: string;
  min?: string; // YYYY-MM-DD lower bound (inclusive)
  max?: string; // YYYY-MM-DD upper bound (inclusive)
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Adds a red border + ring when set — used to highlight a missing
   *  required field. The caller still owns the inline error message. */
  invalid?: boolean;
};

const parseYmd = (value: string): Date | undefined => {
  if (!value) return undefined;
  const [y, m, d] = value.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return undefined;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return undefined;
  return date;
};

const formatYmd = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDisplay = (date: Date): string => {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
};

// Progressive DD-MM-YYYY mask for the typed input: keep only digits (so the
// user can paste "13/07/2026" or type dashes themselves), cap at 8, and
// re-insert dashes between the day / month / year groups.
const maskDdmmyyyy = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  const parts = [
    digits.slice(0, 2),
    digits.slice(2, 4),
    digits.slice(4, 8),
  ].filter(Boolean);
  return parts.join('-');
};

// Parse a fully-typed DD-MM-YYYY string to a Date, rejecting impossible dates
// (round-trips the components so 31-02-2020 → undefined rather than 02-03).
const parseDdmmyyyy = (text: string): Date | undefined => {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(text.trim());
  if (!m) return undefined;
  const d = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const y = parseInt(m[3], 10);
  const date = new Date(y, mo - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== mo - 1 ||
    date.getDate() !== d
  ) {
    return undefined;
  }
  return date;
};

/**
 * Date picker matched to the site theme (#8e2a2f burgundy). Replaces
 * the browser-native `<input type="date">` whose picker can't be
 * styled. The trigger shows DD-MM-YYYY (or the placeholder if empty);
 * the popover uses react-day-picker with burgundy selected/today
 * states, and emits ISO YYYY-MM-DD to the parent.
 */
export function ThemedDatePicker({
  value,
  onChange,
  label,
  min,
  max,
  placeholder = 'dd-mm-yyyy',
  className = '',
  disabled = false,
  invalid = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  // The typed value (DD-MM-YYYY). Seeded from `value`; kept in sync below.
  const [text, setText] = useState(() => {
    const s = parseYmd(value);
    return s ? formatDisplay(s) : '';
  });
  // While the input is focused we let the user type freely and DON'T let an
  // external `value` update clobber their in-progress entry.
  const focusedRef = useRef(false);

  const selected = useMemo(() => parseYmd(value), [value]);
  const minDate = useMemo(() => parseYmd(min ?? ''), [min]);
  const maxDate = useMemo(() => parseYmd(max ?? ''), [max]);

  // Keep the text field in sync when `value` changes from OUTSIDE (a calendar
  // pick, a parent reset) — never while the user is actively typing.
  useEffect(() => {
    if (focusedRef.current) return;
    const s = parseYmd(value);
    setText(s ? formatDisplay(s) : '');
  }, [value]);

  const inRange = (date: Date): boolean =>
    (!minDate || date >= minDate) && (!maxDate || date <= maxDate);

  // Year bounds for the calendar's month/year dropdowns. Derive from min/max
  // when given; otherwise a wide window that covers dates of birth (back ~100y)
  // and near-future picks alike.
  const now = new Date();
  const fromYear = (minDate ?? new Date(now.getFullYear() - 100, 0, 1)).getFullYear();
  const toYear = (maxDate ?? new Date(now.getFullYear() + 10, 11, 31)).getFullYear();

  const handleTextChange = (raw: string) => {
    const masked = maskDdmmyyyy(raw);
    setText(masked);
    if (masked === '') {
      onChange('');
      return;
    }
    const date = parseDdmmyyyy(masked);
    // Emit only a complete, valid, in-range date — partial input leaves the
    // committed value untouched so the parent's required-check stays accurate.
    if (date && inRange(date)) onChange(formatYmd(date));
  };

  const handleBlur = () => {
    focusedRef.current = false;
    // Snap the field back to the canonical value: a half-typed or invalid
    // entry reverts to the last valid date (or clears).
    const s = parseYmd(value);
    setText(s ? formatDisplay(s) : '');
  };

  // Close when clicking outside the wrapper.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (wrapRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const disabledRange = useMemo(() => {
    const ranges: any[] = [];
    if (minDate) ranges.push({ before: minDate });
    if (maxDate) ranges.push({ after: maxDate });
    return ranges.length ? ranges : undefined;
  }, [minDate, maxDate]);

  const handleSelect = (date: Date | undefined) => {
    if (!date) return;
    onChange(formatYmd(date));
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setText('');
    setOpen(false);
  };

  const handleToday = () => {
    const today = new Date();
    if (minDate && today < minDate) return;
    if (maxDate && today > maxDate) return;
    onChange(formatYmd(today));
    setOpen(false);
  };

  const triggerBorder = invalid
    ? 'border-[#fca5a5] ring-1 ring-[#fca5a5]'
    : 'border-[#eee] hover:border-[#8e2a2f]/50';

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      {label ? (
        <span className="block text-[10px] text-[#908e8e] uppercase tracking-wide mb-0.5">
          {label}
        </span>
      ) : null}
      {/* Typeable field: the customer can type the date directly (DD-MM-YYYY)
          — the fast path this component was reworked for. The calendar icon
          still opens the popover for anyone who prefers to click. */}
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={10}
          aria-label={label ?? 'Date'}
          onFocus={() => {
            focusedRef.current = true;
          }}
          onChange={(e) => handleTextChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              setOpen(false);
              (e.target as HTMLInputElement).blur();
            }
          }}
          className={`w-full bg-white border rounded-lg pl-3 pr-10 py-2 text-[13px] font-bold text-[#3a3335] placeholder:text-[#908e8e] placeholder:font-normal outline-none transition-colors ${triggerBorder} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        />
        <button
          type="button"
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Open calendar"
          className={`absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-md ${disabled ? 'cursor-not-allowed' : 'hover:bg-[#f7f7f7] cursor-pointer'}`}
        >
          <span className="material-icons text-[#908e8e] text-[18px]">
            calendar_today
          </span>
        </button>
      </div>

      {open ? (
        <div className="absolute z-50 mt-2 left-0 bg-white rounded-[12px] shadow-[0px_12px_32px_rgba(58,51,53,0.18)] border border-[#eee] p-3 w-[300px]">
          <style>{`
            /* react-day-picker theme overrides for the burgundy palette.
               Scoped to .cp-rdp so we don't leak into other day pickers. */
            .cp-rdp { --rdp-cell-size: 36px; margin: 0; }
            .cp-rdp .rdp-caption_label {
              font-family: 'DM Sans', sans-serif;
              font-weight: 700;
              color: #3a3335;
              font-size: 14px;
            }
            .cp-rdp .rdp-nav_button {
              color: #908e8e;
              border-radius: 8px;
            }
            .cp-rdp .rdp-nav_button:hover {
              background-color: #f7f7f7;
              color: #8e2a2f;
            }
            .cp-rdp .rdp-head_cell {
              font-family: 'DM Sans', sans-serif;
              font-weight: 700;
              color: #908e8e;
              font-size: 11px;
              text-transform: uppercase;
            }
            .cp-rdp .rdp-day {
              font-family: 'DM Sans', sans-serif;
              font-weight: 600;
              color: #3a3335;
              border-radius: 8px;
              font-size: 13px;
            }
            .cp-rdp .rdp-day:hover:not(.rdp-day_selected):not(.rdp-day_disabled) {
              background-color: #fbeaea;
              color: #8e2a2f;
            }
            .cp-rdp .rdp-day_today:not(.rdp-day_selected) {
              color: #8e2a2f;
              font-weight: 800;
              border: 1px solid #8e2a2f;
            }
            .cp-rdp .rdp-day_selected,
            .cp-rdp .rdp-day_selected:focus-visible,
            .cp-rdp .rdp-day_selected:hover {
              background-color: #8e2a2f;
              color: white;
            }
            .cp-rdp .rdp-day_outside {
              color: #c9c5c6;
            }
            .cp-rdp .rdp-day_disabled {
              color: #d4d2d3;
              opacity: 0.6;
            }
            /* Month + year dropdowns (captionLayout="dropdown") — quick jumps
               so a date of birth doesn't take dozens of "prev month" clicks. */
            .cp-rdp .rdp-caption_dropdowns {
              display: flex;
              gap: 6px;
            }
            .cp-rdp .rdp-dropdown {
              font-family: 'DM Sans', sans-serif;
              font-weight: 700;
              color: #3a3335;
              font-size: 13px;
              border: 1px solid #eee;
              border-radius: 8px;
              padding: 4px 6px;
              background: #fff;
              cursor: pointer;
            }
            .cp-rdp .rdp-dropdown:focus-visible {
              outline: none;
              border-color: #8e2a2f;
            }
            .cp-rdp .rdp-vhidden { display: none; }
          `}</style>

          <DayPicker
            className="cp-rdp"
            mode="single"
            selected={selected}
            onSelect={handleSelect}
            defaultMonth={selected ?? maxDate ?? new Date()}
            weekStartsOn={1}
            showOutsideDays
            disabled={disabledRange}
            // Month + year dropdowns for fast navigation (e.g. jumping back
            // decades for a date of birth) instead of one month per click.
            captionLayout="dropdown-buttons"
            fromYear={fromYear}
            toYear={toYear}
          />

          <div className="flex items-center justify-between border-t border-[#eee] mt-1 pt-2 px-1">
            <button
              type="button"
              onClick={handleClear}
              className="text-[#908e8e] text-[12px] font-bold hover:text-[#8e2a2f] transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleToday}
              className="text-[#8e2a2f] text-[12px] font-bold hover:underline"
            >
              Today
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
